import { theme } from 'antd';
import { EnableIcon, TeamIcon, UserIcon } from 'assets/images/svg';
import { SitesIcon } from 'assets/images/svg/Menu';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectTeamsByTeamID } from 'reduxStore/teams/selectors';
import { Col, Collapse, Divider, Drawer, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import ManageTeamDropdown from '../ManageTeamDropdown';
import {
	EditDrawerHeader,
	EditSiteSelect,
	MembersPanelContent,
	MembersPanelExtra,
	PanelHeader,
	PermissionsPanelContent,
	PermissionsPanelExtra,
	TeamDrawerSkeleton
} from './components';

const TeamDrawer = ({ teamID, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Internal state for visibility when not externally controlled
	const [internalVisible, setInternalVisible] = useState(false);

	// ✅ Use external state if provided, otherwise use internal state
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;
	// ✅ Hooks
	const {
		token: { paddingXXL = 48, borderRadiusLG, paddingLG, paddingMD, marginXS, padding }
	} = theme.useToken();

	// Refs
	const membersRef = useRef(null);
	const permissionsRef = useRef(null);

	// ✅ Redux State
	const team = useSelector((state) => selectTeamsByTeamID(state, teamID));

	// ✅ Local State
	const [activeKeys, setActiveKeys] = useState(['permissions', 'members']);
	const [isEditingDependants, setIsEditingDependants] = useState(false);
	const [isEditingMembers, setIsEditingMembers] = useState(false);
	const [isEditingPermissions, setIsEditingPermissions] = useState(false);
	const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
	const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);

	// ✅ Close drawer and reset everything
	const handleOnClose = () => {
		setVisible(false);
	};

	const togglePanel = (key) => {
		setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	};

	const handleCollapseChange = (keys) => {
		// Prevent collapse changes when any modal is open
		if (isMembersModalOpen || isPermissionsModalOpen) {
			return;
		}

		setActiveKeys(keys);

		// If the user closes the panel, we should reset the editing state
		if (!keys.includes('associatedPeople') && isEditingDependants) {
			setIsEditingDependants(false);
		}
	};

	const items = [
		{
			key: 'permissions',
			label: (
				<PanelHeader
					title='Access Permissions'
					iconLeft={<EnableIcon width={16} height={16} />}
					isExpanded={activeKeys.includes('permissions')}
					togglePanel={() => togglePanel('permissions')}
				/>
			),
			children: (
				<div ref={permissionsRef}>
					<PermissionsPanelContent teamID={teamID} isEditing={isEditingPermissions} />
				</div>
			),
			extra: (
				<PermissionsPanelExtra
					teamID={teamID}
					isEditing={isEditingPermissions}
					setIsEditing={setIsEditingPermissions}
					activeKeys={activeKeys}
					setActiveKeys={setActiveKeys}
					wrapperRef={permissionsRef}
					onModalOpenChange={setIsPermissionsModalOpen}
				/>
			)
		},
		{
			key: 'divider-1',
			label: <Divider style={{ margin: 0 }} />,
			children: null,
			collapsible: 'disabled',
			showArrow: false,
			style: { padding: 0 }
		},
		{
			key: 'members',
			label: (
				<PanelHeader
					title='Members'
					iconLeft={<UserIcon />}
					isExpanded={activeKeys.includes('members')}
					togglePanel={() => togglePanel('members')}
				/>
			),
			children: (
				<div ref={membersRef}>
					<MembersPanelContent teamID={teamID} isEditing={isEditingMembers} />
				</div>
			),
			extra: (
				<MembersPanelExtra
					teamID={teamID}
					isEditing={isEditingMembers}
					setIsEditing={setIsEditingMembers}
					activeKeys={activeKeys}
					setActiveKeys={setActiveKeys}
					wrapperRef={membersRef}
					onModalOpenChange={setIsMembersModalOpen}
				/>
			),
			styles: {
				header: {
					marginTop: marginXS
				}
			}
		}
	];

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			styles={drawerStyles}
		>
			{!team ? (
				<TeamDrawerSkeleton />
			) : (
				<Flex vertical>
					<EditDrawerHeader
						teamID={team?.teamID}
						name={team?.name}
						description={team?.description || '-'}
						handleOnCloseEdit={handleOnClose}
					/>
					<div
						style={{
							marginTop: paddingXXL
						}}
					>
						<ManageTeamDropdown teamID={teamID} />
					</div>

					<Divider style={{ margin: `${paddingMD}px 0px` }} />

					<Row>
						<Col span={24}>
							<Flex vertical gap={paddingLG} style={{ padding: padding }}>
								<InfoTuple
									icon={<SitesIcon />}
									text='Site'
									value={<EditSiteSelect siteID={team?.siteID} teamID={team?.teamID} />}
								/>
								<InfoTuple icon={<TeamIcon />} text='Members' value={team?.members?.length} />
							</Flex>
						</Col>
					</Row>

					<Divider style={{ margin: `${paddingMD}px 0px` }} />

					<Collapse
						expandIcon={() => null}
						items={items}
						activeKey={activeKeys}
						onChange={handleCollapseChange}
						ghost
					/>
				</Flex>
			)}
		</Drawer>
	);
};

export default TeamDrawer;
